import React, {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useCallback,
} from 'react';
import { Node } from 'reactflow';
import { ACTIONS_MENU_WIDTH } from '@components/ActionsMenu/constants';
import { clearMultiSelection } from '@components/MainStage/clearMultiSelection';
import { useCollapsedActions } from '@components/MainStage/collapsedLayers.store';
import { getFocusOnState } from '@components/MainStage/focusOn.store';
import useMultiSelectEntity from '@components/MainStage/hooks/useMultiSelectEntity';
import { getMultiSelectionState } from '@components/MainStage/multiSelection.store';
import useStore from '@components/MainStage/store';
import { MenuState } from '@components/MainStage/types';
import {
  NODE_MENU_MARGIN,
  NODE_MENU_Y_HELP_OFFSET,
  NodesTypes,
} from '@constants/canvas/general';
import { subLayerParent, SubLayerTypes } from '@constants/canvas/layers';
import { EditNodeDrawerMap } from '@constants/entities/drawers';
import { ReactFlowId } from '@constants/entities/reports';
import { useProject } from '@context/Project';
import useAssets from '@hooks/useAssets';
import { canvasMap } from '@utils/canvas/CanvasMap';
import { isLayerDisabled } from '@utils/canvasHelpers';
import { isElementLocal } from '@utils/drawers';

const useNodeHandlers = ({
  menu,
  closeMenu,
  openMenu,
  setMenu,
  onPaneClick,
}: {
  menu: MenuState | null;
  closeMenu: () => void;
  openMenu: () => void;
  setMenu: Dispatch<SetStateAction<MenuState | null>>;
  onPaneClick: () => void;
}) => {
  const { selectNodeAndRemoveLocal } = useStore();
  const { isCollapsed } = useCollapsedActions();
  const {
    selectEntity,
    clearMultiSelection: clearSelectionState,
    setSingleSelectionAnchor,
  } = useMultiSelectEntity();

  const {
    setDrawer,
    drawer,
    toolbox: { mode },
  } = useProject();

  const { selectedAssetId } = useAssets();

  const onMove = useCallback(() => {
    if (menu) closeMenu();
  }, [menu]);

  const onNodeClick = useCallback(
    (event: MouseEvent, newNode: Node) => {
      if (newNode?.type !== NodesTypes.CustomNode) {
        onPaneClick();
        return;
      }

      const focus = getFocusOnState();
      if (focus.sourceId && !focus.isFocused(newNode.id)) return;

      if (menu) closeMenu();

      // Shift-click enters / continues the multi-selection.
      if (event.shiftKey) {
        selectEntity(newNode.id);
        return;
      }

      clearSelectionState();

      if (!focus.sourceId) canvasMap.focusCurrentNode(newNode.id);

      const layerId = subLayerParent[newNode.parentNode as SubLayerTypes];
      selectNodeAndRemoveLocal(newNode.id);
      setDrawer({ type: EditNodeDrawerMap[layerId] });
      // Remember this entity so a following Shift-click keeps it selected.
      setSingleSelectionAnchor(newNode.id);
    },
    [onPaneClick, selectEntity, clearSelectionState, setSingleSelectionAnchor],
  );

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault();
      if (isElementLocal(node.id)) return;

      const { type = '', id } = node;

      const focus = getFocusOnState();
      if (focus.sourceId) {
        if (type === NodesTypes.SubLayerNode) return;
        if (type === NodesTypes.CustomNode && !focus.isFocused(id)) return;
      }

      const pane = document
        .querySelector(`#${ReactFlowId}`)
        ?.getBoundingClientRect();

      if (!pane) return;

      if (type === NodesTypes.CustomNode) {
        const nodeDOM = document.querySelector<HTMLElement>(
          `[data-id="${id}"]`,
        );

        if (!nodeDOM) return;

        const nodeRect = nodeDOM.getBoundingClientRect();
        const rightSpace = pane.right - nodeRect.right;
        const isNotEnoughSpace =
          rightSpace < ACTIONS_MENU_WIDTH + NODE_MENU_MARGIN;

        const top = nodeRect.y - NODE_MENU_Y_HELP_OFFSET + nodeRect.height / 2;

        const left = isNotEnoughSpace
          ? nodeRect.left - ACTIONS_MENU_WIDTH - NODE_MENU_MARGIN
          : nodeRect.right + NODE_MENU_MARGIN;

        const { selectedIds } = getMultiSelectionState();
        if (selectedIds.length > 0 && !selectedIds.includes(id)) {
          clearMultiSelection();
        }

        setMenu({
          id: node.id,
          type: 'node',
          top,
          left,
          position: 'fixed',
        });

        openMenu();
        return;
      }

      if (
        type === NodesTypes.SubLayerNode &&
        mode &&
        !isCollapsed(node.id as SubLayerTypes) &&
        !isLayerDisabled(id, mode, selectedAssetId)
      ) {
        setMenu({
          id: node.id,
          type: 'subLayer',
          top: event.clientY - pane.top,
          left: event.clientX - pane.left,
        });

        openMenu();
        return;
      }

      if (drawer) return;

      setMenu({
        id: 'canvas',
        type: 'canvas',
        top: event.clientY - pane.top,
        left: event.clientX - pane.left,
      });

      openMenu();
    },
    [mode, selectedAssetId, drawer],
  );

  return { onNodeClick, onMove, onNodeContextMenu };
};

export default useNodeHandlers;
